import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getInternalNote } from "utils/application.utils";

/**
 * How much of a candidate goes to the owner (DEV-3326).
 *
 * The levels are cumulative and the order matters: each one adds to the one
 * before it, which is what lets a single comparison decide whether a field
 * travels.
 */
export enum ESendToOwnerLevel {
  Basic = "BASIC",
  Assessment = "ASSESSMENT",
}

/** The server's limit on the intro and on each comment. */
export const MAX_TEXT_LENGTH = 2000;

export const SEND_TO_OWNER_LEVELS = [
  ESendToOwnerLevel.Basic,
  ESendToOwnerLevel.Assessment,
];

/**
 * The broker's own judgement: their stars and their comment on this
 * candidate. Shown and sent from the assessment level up, so a broker who
 * picked "basic" cannot send their private opinion by accident.
 */
export const includesAssessment = (level: ESendToOwnerLevel) =>
  level !== ESendToOwnerLevel.Basic;

/**
 * The link to the full file is no longer a level but a switch beside them,
 * on by default and available at both (Jonas, 2026-10-06). It was a third
 * level, which made "how much of my opinion do I share" and "may the owner
 * open the file" one choice when they are two.
 */

export interface ISendToOwnerInput {
  propertyId: number;
  applications: IApplication[];
  recipientAccountIds: number[];
  level: ESendToOwnerLevel;
  includeProfileLink: boolean;
  /** Whether the broker also wants the mail in their own inbox. */
  ccSender: boolean;
  introText?: string;
  commentByApplicationId: { [applicationId: string]: string };
}

export interface ISendToOwnerPayload {
  propertyId: number;
  applicationIds: string[];
  recipientAccountIds: number[];
  level: ESendToOwnerLevel;
  includeProfileLink: boolean;
  ccSender: boolean;
  introText?: string;
  comments?: { applicationId: string; comment: string }[];
}

/**
 * The comment each box starts with: the note the broker already wrote about
 * this candidate, resolved exactly as the candidate row resolves it, so the
 * window opens with the words the broker is looking at rather than with a
 * second, slightly different idea of "the notes".
 */
export const getPrefilledComments = (applications: IApplication[]) => {
  const comments: { [applicationId: string]: string } = {};

  applications.forEach(application => {
    comments[`${application.id}`] = getInternalNote(application);
  });

  return comments;
};

/**
 * What the dashboard sends.
 *
 * Two rules are enforced here rather than left to the server, because this
 * mail is a copy that can never be withdrawn: at the basic level no comment
 * leaves the dashboard at all, and an empty comment is dropped rather than
 * sent as an empty line under a candidate's name. The stars are not in the
 * payload; the server reads them from the application, so the two can never
 * disagree about what the broker rated.
 *
 * The candidate's email, phone and identity data are not here either, and
 * neither is the Rentmatch score, which Rentmatch promises is never shared
 * with a landlord without the candidate's consent.
 */
export const buildSendToOwnerPayload = ({
  propertyId,
  applications,
  recipientAccountIds,
  level,
  includeProfileLink,
  ccSender,
  introText,
  commentByApplicationId,
}: ISendToOwnerInput): ISendToOwnerPayload => {
  const applicationIds = applications.map(application => `${application.id}`);

  const payload: ISendToOwnerPayload = {
    propertyId,
    applicationIds,
    recipientAccountIds,
    level,
    includeProfileLink,
    ccSender,
  };

  // Not sliced. The intro is the editor's HTML, and cutting it at a
  // character count can land inside a tag and send half an element; the
  // window refuses to send an intro that is too long instead, measured on
  // the text an owner reads rather than on the markup (DEV-3339).
  if (introText?.trim()) {
    payload.introText = introText.trim();
  }

  if (includesAssessment(level)) {
    const comments = applicationIds
      .map(applicationId => ({
        applicationId,
        comment: `${commentByApplicationId[applicationId] || ""}`
          .trim()
          .slice(0, MAX_TEXT_LENGTH),
      }))
      .filter(({ comment }) => !!comment);

    if (comments.length) {
      payload.comments = comments;
    }
  }

  return payload;
};

/**
 * What to tell the broker after a send that worked.
 *
 * A full send where some candidates have no file yet is a success with a
 * caveat, not a failure: the others did go out with their link. Saying only
 * "sent" would leave the broker to discover the gap from the owner.
 */
export const getSuccessMessage = (
  result: {
    recipients: number;
    applicationsWithoutPdf?: string[];
  },
  includeProfileLink: boolean,
) => {
  // Only worth saying when a link was asked for: without one, a candidate
  // having no file yet changes nothing about what the owner received.
  const withoutFile = includeProfileLink
    ? result.applicationsWithoutPdf?.length || 0
    : 0;

  return withoutFile
    ? {
        key: "application.send_to_owner.success.without_file",
        params: { count: `${withoutFile}` },
      }
    : {
        key: "application.send_to_owner.success",
        params: { count: `${result.recipients}` },
      };
};

/**
 * What to tell the broker after a refusal.
 *
 * The server's own words where it gave any, because its refusals name the
 * thing that is wrong, such as a candidate belonging to another property.
 * Nothing is sent in part, so the fallback says so plainly rather than
 * leaving the broker wondering whether half of it went out.
 */
export const getErrorMessage = (serverMessage: string) =>
  serverMessage.trim() || undefined;
